// The account file's derivations (module 15 PLAN §4 to §6; the drawings of 2026-09-20,
// module 10 DESIGN B864 to B871 and B896 to B905). Pure functions, so every line the page
// states can be tested without a browser or a database.
//
// The rule these obey, and the reason they are functions rather than JSX: a line on this page
// is derived from a named field of the read, and a line that would need a join the read does
// not make is not written. Four claims on the drawings were inferred before this rule was
// applied, and each had to be taken off.

import { plateDay } from './company-read';
import type { CompanyRecordView } from '../api';

export type Clock = { days: number; words: string; warm: boolean };

/**
 * A clock is warm (red) inside 90 days, and once its date has passed. Mike chose the 90 by pop-up on
 * 5 Oct 2026, typing "Red within 90 days" (U324): the drawing of 20 Sep (B867) had made it red inside
 * 120, which put "87 days to run" and a renewal four months off in the colour the look keeps for live
 * and silent things. The words still count days to 120 and months beyond; only the colour moved.
 */
export const WARM_DAYS = 90;

/** Whole days from `from` to `on`, and the words for a clock chip. Warm inside WARM_DAYS. */
export function clockTo(on: string, from: Date): Clock | null {
  const end = Date.parse(on.length <= 10 ? `${on}T00:00:00Z` : on);
  if (Number.isNaN(end)) return null;
  const start = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate());
  const days = Math.round((end - start) / 86_400_000);
  if (days < 0) return { days, words: `${plural(-days, 'day')} ago`, warm: true };
  if (days <= 120) return { days, words: `${plural(days, 'day')} to run`, warm: days <= WARM_DAYS };
  const months = Math.floor(days / 30.44);
  return { days, words: `${plural(months, 'month')} to run`, warm: false };
}

/* ------------------------------------------------------- the signed paper */

/** The endings a file name carries, read off the end one at a time: "x.docx.pdf" has two. */
const ENDING = /\.(pdf|docx?|xlsx?|pptx?|rtf|odt|txt|msg|eml)$/i;
/**
 * A version mark at the end of a name: "(final)" in brackets, "final" in any case joined on by an
 * underscore or a hyphen, or FINAL in capitals after a space. Never " Final" after a space, which is
 * a word ("World Cup Final") and stays.
 */
const FINAL_MARK = /(?:[\s_\-–]*\((?:final|Final|FINAL)\)|[\s_\-–]*[_\-–][\s_\-–]*(?:final|Final|FINAL)|\s*\bFINAL)$/;

/**
 * A signed paper's file name said as words (U324): the endings go, underscores become spaces and a
 * trailing FINAL goes, so "iGame-bet365 MSS License Agreement_FINAL.docx.pdf" reads "iGame-bet365
 * MSS License Agreement". The rest is the paper's own name and is not ours to restyle: it is what
 * tells a reader which paper sits behind a register row, and Bet365's "Content Licencing" row opens
 * the MSS licence. The same rule as a note's file name on Deals (subjectWords, P10).
 */
export function paperWords(name: string): string {
  let s = name.trim();
  while (ENDING.test(s)) s = s.replace(ENDING, '');
  const spaced = (x: string) => x.replace(/_+/g, ' ').replace(/\s+/g, ' ').trim();
  const words = spaced(s.replace(FINAL_MARK, ''));
  return words || spaced(s) || name;
}

/** What kind of file the paper is, from its type and failing that its last ending, or nothing. */
export function paperKind(mime: string | null, name: string): string | null {
  const m = (mime ?? '').toLowerCase();
  if (m === 'application/pdf') return 'PDF';
  if (m.includes('wordprocessingml') || m === 'application/msword') return 'Word';
  if (m.includes('spreadsheetml') || m === 'application/vnd.ms-excel') return 'Excel';
  const end = /\.([a-z]+)$/i.exec(name.trim())?.[1]?.toLowerCase();
  return end === 'pdf' ? 'PDF' : end === 'doc' || end === 'docx' ? 'Word' : end === 'xls' || end === 'xlsx' ? 'Excel' : null;
}

/** A file size as a person reads it. */
export function fileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} bytes`;
}

/** The line beside "Open the signed agreement": "iGame-bet365 MSS License Agreement: PDF, 349 KB" (no middle dot, BUGS.md D172). */
export function paperLine(doc: { name: string; mime: string | null; sizeBytes: number | null }): string {
  const facts = [paperKind(doc.mime, doc.name), doc.sizeBytes ? fileSize(doc.sizeBytes) : null].filter((x): x is string => x !== null);
  return facts.length ? `${paperWords(doc.name)}: ${facts.join(', ')}` : paperWords(doc.name);
}

export function plural(n: number, word: string, many?: string): string {
  if (n === 1) return `${n} ${word}`;
  // "address" and "reply" do not take a bare s, and the page printed "8 shared addresss" until
  // this had a rule rather than a concatenation.
  const s = many
    ?? (/(s|x|z|ch|sh)$/.test(word) ? `${word}es`
      : /[^aeiou]y$/.test(word) ? `${word.slice(0, -1)}ies`
      : `${word}s`);
  return `${n.toLocaleString('en-GB')} ${s}`;
}

/* ---------------------------------------------------------------- people */

export type Named = {
  id: string; name: string; jobTitle: string | null; isUser: boolean;
  /** When this person was last in a message the reader may see, and how many. Null means never seen. */
  lastAt: string | null; messages: number;
  /** BUGS.md D75: the reader said this is a person, with the action Undo takes back and until when. */
  said?: { actionId: string; until: string } | null;
};

function localPart(raw: string): string {
  const bare = raw.replace(/^['"\s]+|['"\s]+$/g, '');
  const at = bare.indexOf('@');
  return at === -1 ? bare : bare.slice(0, at);
}

/**
 * The name Cobrain should show. An address that arrived as a name becomes the person it names;
 * a name that arrived wrapped in quotes loses them. Nothing else is changed, because a real
 * name is the source's and not ours to restyle.
 */
export function cleanName(raw: string): string {
  const bare = raw.replace(/^['"\s]+|['"\s]+$/g, '');
  if (!bare) return raw;
  if (!bare.includes('@')) return bare;
  const local = localPart(bare);
  const parts = local.split(/[._\-+]+/).filter(Boolean);
  if (parts.length < 2) return bare;                       // one word tells us nothing: keep the address
  if (parts.some((p) => /\d/.test(p))) return bare;        // a digit means it is not a name
  // BUGS.md D183: a part written in CamelCase keeps its capitals ("ControlDesk", not "Controldesk"); only a part in one
  // case is put in title case.
  const word = (p: string) => (/[a-z]/.test(p) && /[A-Z]/.test(p.slice(1)) ? p.charAt(0).toUpperCase() + p.slice(1) : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase());
  return parts.map(word).join(' ');
}

export type PeopleSplit = { people: Named[]; shared: Named[] };

/** People on one side, the desks and mailboxes on the other, each name cleaned. */
export function splitPeople(rows: ReadonlyArray<{ id: string; name: string; jobTitle: string | null; isUser: boolean; lastAt?: string | null; messages?: number; saidPerson?: boolean; said?: { actionId: string; until: string } | null }>): PeopleSplit {
  const people: Named[] = [];
  const shared: Named[] = [];
  for (const r of rows) {
    const row: Named = { id: r.id, name: cleanName(r.name), jobTitle: r.jobTitle, isUser: r.isUser, lastAt: r.lastAt ?? null, messages: r.messages ?? 0, said: r.said ?? null };
    // The API returns only centrally classified people here; desks arrive in filedApart.
    // Reclassifying by a substring of a name hid real people such as Torsten Billing.
    people.push(row);
  }
  // PEOPLE are ordered by who we heard from last, because that is the order this section is read in;
  // alphabetical put Aleksandra Lipowska above the chief executive we actually deal with. Somebody
  // never seen in a message sorts to the bottom rather than the top, where a null would put them.
  const byHeard = (a: Named, b: Named) =>
    (b.lastAt ?? '').localeCompare(a.lastAt ?? '') || a.name.localeCompare(b.name, 'en-GB');
  const byName = (a: Named, b: Named) => a.name.localeCompare(b.name, 'en-GB');
  return { people: people.sort(byHeard), shared: shared.sort(byName) };
}

/* ---------------------------------------------------- conversations */

const NOISE = /^(automatic reply|auto ?reply|out of office|accepted|declined|tentative|canceled|cancelled):/i;
const REPLY = /^\s*((re|aw|fwd?|tr|sv|vs|antw)\s*(\[\d+\])?\s*:\s*)+/i;

export type Thread = {
  key: string;
  title: string;
  count: number;
  firstAt: string;
  lastAt: string;
  lastOutbound: boolean;
  everyOneAReply: boolean;
  kind: string;
  /** Who wrote the newest message in the thread, and how it opens. */
  lastWho: string | null;
  lastOpens: string | null;
  /** The newest message, and where it opens, where the API read the thread whole (BUGS.md D166); absent otherwise. */
  lastId?: string;
  open?: { page: 'item' | 'meeting' | 'chat'; id: string } | null;
  /** Cobrain's two lines on it, where it stands and what is owed (B2355); absent or null where there are none. */
  summary?: { stands: string; owed: string | null; asOf: string; fresh: boolean } | null;
};

export type Conversations = { threads: Thread[]; noise: number; shown: number; held: number; total: number };

/**
 * The newest messages grouped into threads by their subject, with the automatic replies and the
 * calendar acceptances dropped by rule, at no model cost. What this cannot say is who is on a
 * thread: the company read returns a subject, a time and a direction and no participant.
 */
export function threadsOf(recent: ReadonlyArray<{ id: string; occurredAt: string; kind: string; subject: string | null; direction: string | null; who?: string | null; opens?: string | null }>, total: number): Conversations {
  const map = new Map<string, Thread>();
  let noise = 0;
  for (const s of recent) {
    const subject = (s.subject ?? '').trim();
    if (NOISE.test(subject)) { noise += 1; continue; }
    const title = subject.replace(REPLY, '').trim();
    const key = `${s.kind}:${title.toLowerCase()}`;
    const isReply = REPLY.test(subject);
    const t = map.get(key);
    if (!t) {
      map.set(key, {
        key, title: title || '(no subject)', count: 1,
        firstAt: s.occurredAt, lastAt: s.occurredAt,
        lastOutbound: s.direction === 'outbound', everyOneAReply: isReply, kind: s.kind,
        // The newest message's own words and writer, so a thread is communication and not a subject.
        lastWho: s.who ?? null, lastOpens: s.opens ?? null,
      });
      continue;
    }
    t.count += 1;
    t.everyOneAReply = t.everyOneAReply && isReply;
    if (s.occurredAt > t.lastAt) { t.lastAt = s.occurredAt; t.lastOutbound = s.direction === 'outbound'; t.lastWho = s.who ?? null; t.lastOpens = s.opens ?? null; }
    if (s.occurredAt < t.firstAt) t.firstAt = s.occurredAt;
  }
  const threads = [...map.values()].sort((a, b) => (a.lastAt < b.lastAt ? 1 : -1));
  // `shown` is what a reader can see; `held` is every row this page was handed, noise included,
  // and the number NOT listed is measured against that. Taking the noise off both said 74 older
  // messages where 72 are held.
  return { threads, noise, shown: recent.length - noise, held: recent.length, total };
}

/**
 * The threads as the API read them whole (BUGS.md D166): the newest twelve, each counted in full, so Bet365's ninety
 * messages are its threads and not the newest ten messages of two. `held` is every message in the threads listed, and
 * what the page says is not listed is measured against it.
 */
export function threadsRead(rows: ReadonlyArray<{ id: string; kind: string; subject: string | null; count: number; firstAt: string; lastAt: string; lastOutbound: boolean; everyOneAReply: boolean; who: string | null; opens: string | null; open: { page: 'item' | 'meeting' | 'chat'; id: string } | null; summary?: { stands: string; owed: string | null; asOf: string; fresh: boolean } | null }>, total: number): Conversations {
  const threads: Thread[] = rows.map((r) => ({
    key: r.id, title: (r.subject ?? '').trim().replace(REPLY, '').trim() || '(no subject)', count: r.count,
    firstAt: r.firstAt, lastAt: r.lastAt, lastOutbound: r.lastOutbound, everyOneAReply: r.everyOneAReply, kind: r.kind,
    lastWho: r.who, lastOpens: r.opens, lastId: r.id, open: r.open, summary: r.summary ?? null,
  }));
  const held = threads.reduce((a, t) => a + t.count, 0);
  return { threads, noise: 0, shown: held, held, total };
}

/** Days between the two ends of a thread, for "six messages over twelve days". */
export function spanDays(a: string, b: string): number {
  return Math.max(0, Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000));
}

/* ------------------------------------------------------------- the brief */

export type BriefLine = { label: string; text: string; under?: string | undefined; warmWord?: string | undefined };
export type Brief = { lines: BriefLine[]; foot: string[] };

/**
 * The brief in the order Mike chose for the call panel (module 13 L25), printing only the
 * headings that have something and naming the empty ones at its foot (U113).
 */
export function briefOf(c: CompanyRecordView, now: Date): Brief {
  const lines: BriefLine[] = [];
  const foot: string[] = [];

  const inForce = agreementsInForce(c, now);
  if (inForce.length) {
    const soonest = inForce.reduce((a, b) => (a.clock && b.clock && b.clock.days < a.clock.days ? b : a));
    const sameDay = soonest.endDate ? inForce.filter((x) => x.endDate === soonest.endDate).length : 0;
    const which = sameDay > 1 ? `${countWord(sameDay)} of them end` : inForce.length === 1 ? 'It ends' : 'The first ends';
    const head = `${countWord(inForce.length)} ${inForce.length === 1 ? 'agreement' : 'agreements'} in force.`;
    const ends = soonest.endDate && soonest.clock ? formatDay(soonest.endDate) : null;
    // The end date is red by the clock's own rule (U324), never whatever its distance: a renewal
    // twenty months off was red here while its clock beside it was the dark plate.
    const warm = Boolean(ends && soonest.clock?.warm);
    lines.push({
      label: 'At stake',
      text: !ends ? head : warm ? `${head} ${which} on ` : `${head} ${which} on ${ends}.`,
      warmWord: warm ? ends! : undefined,
      under: soonest.clock ? `${soonest.clock.days} days away.` : undefined,
    });
  } else {
    foot.push('no agreement in force');
  }

  // BUGS.md D10: the API's own read of the last contact, a show meeting included and a diary invitation not; an older
  // API sends none, and the newest message stands in.
  const last = c.lastSpoke !== undefined
    ? c.lastSpoke && { occurredAt: c.lastSpoke.at, kind: c.lastSpoke.kind, direction: c.lastSpoke.direction, show: c.lastSpoke.show }
    : c.recent[0] ? { ...c.recent[0], show: null } : null;
  if (last) {
    const days = spanDays(last.occurredAt, now.toISOString());
    const how = last.show ? `a meeting at ${last.show}` : last.kind === 'meeting' || last.kind === 'call' ? 'a call' : last.direction === 'outbound' ? 'and you sent it' : 'and they sent it';
    lines.push({
      label: 'Since you last spoke',
      text: `${days === 0 ? 'Spoken to today' : `${plural(days, 'day')} of silence`}. The last was on ${formatDay(last.occurredAt)}, ${how}.`,
      under: `${plural(c.recentTotal, 'message')} held with them.`,
    });
  } else {
    foot.push('no message or meeting with them');
  }

  const w = c.usage?.oneTrade?.window ?? null;
  if (w && w.activeUsers !== null && w.activeUsers !== undefined) {
    const prev = w.previousActiveUsers ?? null;
    // BUGS.md D163: one measure under one rule. This line is the last thirty days against the thirty before, and says so,
    // so it never reads as a contradiction of Usage's month against the same month a year ago; inside 2% it is level.
    const level = prev !== null && prev > 0 && Math.abs(w.activeUsers - prev) / prev < 0.02;
    const dir = prev === null ? '' : level ? ', level with the thirty days before' : w.activeUsers >= prev ? ', up on the thirty days before' : ', down on the thirty days before';
    // BUGS.md D13, D140: nobody in either window is no use, never "up" from 0 to 0.
    const none = w.activeUsers === 0 && prev === 0;
    lines.push({
      label: 'How they are using it',
      text: none ? 'No use in the last thirty days, nor in the thirty days before.' : `${w.activeUsers.toLocaleString('en-GB')} people used it in the last thirty days${dir}.`,
      under: none ? 'The last thirty days against the thirty before; Usage below compares months a year apart.' : prev === null
        ? 'The last thirty days; Usage below compares months a year apart.'
        : `${prev.toLocaleString('en-GB')} in the thirty before. The last thirty days against the thirty before; Usage below compares months a year apart.`,
    });
  } else {
    foot.push('nothing that measures use is joined to this record');
  }

  // Said only where the sections below have nothing either (BUGS.md D41): Operations lists an incident naming them,
  // Press and intelligence an item naming them.
  if (!c.operations || c.operations.recent.length === 0) foot.push('nothing from operations');
  if (!c.press || c.press.named === 0) foot.push('nothing in the trade press about them');
  return { lines, foot };
}

/** BUGS.md D154: a call or a meeting is listed under Meetings, never as a written thread ('they wrote last'). */
export function isWrittenThread(kind: string): boolean {
  return kind !== 'calendar_event' && kind !== 'meeting' && kind !== 'call';
}

/** The Conversations heading: the written threads and the calls Cobrain holds with the company, never fixed words (D154). */
export function conversationsState(threads: number, calls: number, diary: number): string {
  if (threads === 0 && calls === 0) return diary ? 'No thread, one diary entry' : 'Nothing held';
  const t = threads ? `${countWord(threads)} ${threads === 1 ? 'thread' : 'threads'}` : 'No thread';
  return `${t}, ${calls ? `${countWord(calls).toLowerCase()} ${calls === 1 ? 'call' : 'calls'} recorded` : 'no call recorded'}`;
}

export function countWord(n: number): string {
  const words = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
  return n <= 10 ? words[n]! : n.toLocaleString('en-GB');
}

export function formatDay(iso: string): string {
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCDate()} ${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** More than this many days past its date and an unaccepted reading is folded, as Tasks folds (tasks/order.ts). */
export const OWED_FOLD_DAYS = 30;

/**
 * Owed each way, in the order the section draws it, and Cobrain's old readings folded away (BUGS.md D16; module 04
 * B2166). A promise Cobrain read and nobody accepted, more than a month past its date, is evidence nobody closed it,
 * not something owed today: Kaizen's record led with one due 12 October 2025. Mike chose by pop-up on 6 Oct ("Fold
 * them, as Tasks does"): those go under one closed line, newest first; an accepted promise stays however late it is.
 * What shows runs the freshest slip first, then what is ahead soonest first, then what carries no date. Days are the
 * due date's own and today's on UTC's calendar, as the rest of this section counts them.
 */
export function owedFold<T extends { dueAt: string | null; status: string }>(rows: readonly T[], now: Date): { shown: T[]; folded: T[] } {
  const today = now.toISOString().slice(0, 10);
  const edge = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - OWED_FOLD_DAYS * 86_400_000).toISOString().slice(0, 10);
  const day = (w: T): string | null => (w.dueAt ? w.dueAt.slice(0, 10) : null);
  const newestFirst = (a: T, b: T): number => (day(b)! < day(a)! ? -1 : day(b)! > day(a)! ? 1 : 0);
  const folded = rows.filter((w) => w.status === 'suggested' && day(w) !== null && day(w)! < edge).sort(newestFirst);
  const rest = rows.filter((w) => !folded.includes(w));
  const late = rest.filter((w) => day(w) !== null && day(w)! < today).sort(newestFirst);
  const ahead = rest.filter((w) => day(w) !== null && day(w)! >= today).sort((a, b) => -newestFirst(a, b));
  const undated = rest.filter((w) => day(w) === null);
  return { shown: [...late, ...ahead, ...undated], folded };
}

/**
 * A deal removed from the Deals desk, as the company page marks it (U156; Mike by pop-up 2026-09-24): the day, in the
 * reader's own clock since it is a moment and not a calendar date, and who removed it when the reader may read that.
 */
export function removedWords(at: string, by: string | null): string {
  const d = new Date(at);
  const day = Number.isNaN(d.getTime()) ? at
    : `${d.getDate()} ${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][d.getMonth()]} ${d.getFullYear()}`;
  return by ? `Removed ${day} by ${by}` : `Removed ${day}`;
}

/* ------------------------------------------------------------ agreements */

export type Agreement = {
  id: string;
  title: string;
  /** The day it ends or next renews: the register's end date, carried on by its term where it renewed by itself. */
  endDate: string | null;
  clock: Clock | null;
  /**
   * Its register end date has passed and it does not renew by itself (U325): it is drawn under Ended, in grey, and
   * counted nowhere as in force. BetInvest's Content Services Agreement, ended 12 August 2025, was "in force" with
   * "-418 days away" beside it until this (BUGS.md D7).
   */
  ended: boolean;
  /**
   * Set where it renews by itself and the register's end date has passed (U326): the day it last renewed, the
   * register's own end date, and the first term in months its next date was worked from (null where the register
   * gives no term, and then there is no next date).
   */
  renewed: Renewal | null;
  /**
   * Whether the register says it renews by itself; null where the register does not say. The card labelled every
   * date 'Renews', so Bet365's Uruguay licence, which does not renew, read 'RENEWS 31 December 2026' and InPlay, which
   * does, 'Ends 31 August 2028' (BUGS.md D160). endWords turns it into the label.
   */
  autoRenew: boolean | null;
  entity: string | null;
  start: string | null;
  billed: boolean;
  /** The register's own words for the fee, unparsed: "£480,000", "RevShare", "100,000.00 €". */
  feeText: string | null;
  fee: Fee | null;
  /** The signed document Cobrain ingested, if it has one: the item page can open it. */
  documentSignalId: string | null;
  /** The signed paper behind this row, where the Contracts tree holds one. */
  document: { name: string; url: string | null; mime: string | null; sizeBytes: number | null; held: boolean; sharedWith: number; namesOther?: string[] } | null;
  /** The register's link field. Free text: sometimes a URL, often a reference like "1003 - bet365". */
  contractLink: string | null;
  /**
   * What finance booked against THIS agreement, from its own product line -- never the company's
   * total. The live page put £965,428 on Bet365's £1,000-a-month InPlay row, because any agreement
   * that matched an Anaplan term was shown `billedLast12`, which is every line the company has.
   * The Modus content deal that actually earns £873,290 said "No billing line of its own" three
   * rows above it. `shared` is how many agreements claim the same line: where more than one does,
   * the money is named and no single row is allowed to own it.
   */
  booked: { product: string; amount: number; shared: number } | null;
  /**
   * Set where the register names a rights holder we resell for AND the competition being licensed.
   * Six rows of 150 do, all five Media Pro rows and the Modus one, and NO other row names a
   * competition -- finance fills both columns on purpose, so this is a statement of theirs and not
   * a guess of ours. The fee on such a row is the SEASON's, and the money finance books is our
   * share of it, against the rights holder rather than against this company (B1533).
   */
  season: { competition: string; rightsHolder: string } | null;
  /** The register's notice period in days, or null where it holds none (BUGS.md D162). */
  noticeDays: number | null;
};

/**
 * A fee the register states as a figure. Rev-share and variable fees are NOT this: they come back
 * null with `feeText` kept, because a deal whose value depends on the client's turnover has a real
 * value and it is not zero. Nothing here converts a currency -- there is no rate in the store, and
 * inventing one would be inventing a figure -- so a euro fee stays in euro and is never added to a
 * sterling one.
 */
export type Fee = { amount: number; currency: 'GBP' | 'EUR' | 'USD'; per: 'month' | 'season' };

const SYMBOL: Record<string, Fee['currency']> = { '£': 'GBP', '€': 'EUR', '$': 'USD' };

/**
 * The register's fee column, read as strictly as it deserves. 137 of 150 rows carry one, in 81
 * distinct shapes. A row is a figure only if exactly one currency symbol appears and what is left
 * parses whole; anything else ("RevShare", "Variable", a blank) is not a figure and is not a zero.
 */
export function parseFee(raw: string | null, per: Fee['per'] = 'month'): Fee | null {
  if (!raw) return null;
  const text = raw.trim();
  const symbols = [...new Set([...text].filter((ch) => ch in SYMBOL))];
  if (symbols.length !== 1) return null;
  const currency = SYMBOL[symbols[0]!]!;
  const digits = text.replace(symbols[0]!, '').replace(/,/g, '').trim();
  if (!/^\d+(\.\d+)?$/.test(digits)) return null;
  const amount = Number(digits);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return { amount, currency, per };
}

const SIGN: Record<Fee['currency'], string> = { GBP: '£', EUR: '€', USD: '$' };

/** A fee as a person reads it: the month, and the year it implies, in its own currency. */
export function feeLine(fee: Fee): { month: string; year: string } {
  const show = (n: number) => `${SIGN[fee.currency]}${n.toLocaleString('en-GB', { maximumFractionDigits: n % 1 === 0 ? 0 : 2 })}`;
  // A season fee is the whole fee. Multiplying it by twelve is what turned Bet365's £480,000
  // Uruguay licence into £5,760,000 a year and the company's register total into £5,916,000.
  if (fee.per === 'season') return { month: show(fee.amount), year: `${show(fee.amount)} for the season` };
  return { month: `${show(fee.amount)} a month`, year: `${show(fee.amount * 12)} a year` };
}

/**
 * What the agreements in force are contracted to be worth in a year, per currency, and what is
 * left out. A total that silently dropped the rev-share rows would be the same kind of lie as the
 * one this replaces, so the count of what carries no figure is returned beside it and the page
 * prints it.
 */
export function contractedAYear(rows: Agreement[]): { totals: Array<{ currency: Fee['currency']; amount: number }>; agency: Array<{ currency: Fee['currency']; amount: number }>; unpriced: Agreement[] } {
  const by = new Map<Fee['currency'], number>();
  const agencyBy = new Map<Fee['currency'], number>();
  const unpriced: Agreement[] = [];
  for (const a of rows) {
    if (!a.fee) { unpriced.push(a); continue; }
    const amount = a.fee.per === 'season' ? a.fee.amount : a.fee.amount * 12;
    // An agency row is kept APART from the total, never added into it and never dropped. Finance
    // books our share of it against the rights holder, so adding it to what this company is
    // contracted to pay US and then asking finance to bear that out compares two different things
    // -- which is how the heading still read "£504,000 a year, which finance does not bear out"
    // after the £5,916,000 had been mended (B1534).
    const m = a.season ? agencyBy : by;
    m.set(a.fee.currency, (m.get(a.fee.currency) ?? 0) + amount);
  }
  const order: Fee['currency'][] = ['GBP', 'EUR', 'USD'];
  const line = (m: Map<Fee['currency'], number>) => order.filter((c) => m.has(c)).map((c) => ({ currency: c, amount: m.get(c)! }));
  return { totals: line(by), agency: line(agencyBy), unpriced };
}

/** A total as a line, in every currency it is made of, never added together. */
export function totalLine(totals: Array<{ currency: Fee['currency']; amount: number }>): string {
  return totals.map((t) => `${SIGN[t.currency]}${t.amount.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`).join(' and ');
}

export type Renewal = { on: string; firstEnd: string; termMonths: number | null };

/** A register term in months ("12", "24 months"), or nothing: anything else is not a term this page can count on. */
export function termMonths(raw: string | null | undefined): number | null {
  const m = /^\s*(\d{1,3})\s*(?:months?)?\s*$/i.exec(raw ?? '');
  const n = m ? Number(m[1]) : Number.NaN;
  return Number.isInteger(n) && n >= 1 ? n : null;
}

/** A day moved on by whole months, held to the month's last day: 29 February 2024 and twelve is 28 February 2025. */
export function addMonths(day: string, months: number): string {
  const [y, m, d] = day.slice(0, 10).split('-').map(Number) as [number, number, number];
  const t = m - 1 + months;
  const year = y + Math.floor(t / 12);
  const month = ((t % 12) + 12) % 12;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
}

/**
 * Where an agreement stands on `now` against its register end date. Until the day itself it runs to that date. Past
 * it, one that renews by itself has renewed (U326: "Note that it auto renewed and set the new auto renew date"), and
 * its next date is the end date carried on by its first term until it is today or later -- the only term the
 * register holds, and the card says that is how it was worked out. One that does not renew by itself has ended
 * (U325). A register that does not say whether it renews is read as it is written: an end date, and nothing after.
 */
export function standing(endDate: string | null, autoRenew: boolean | null, term: string | null | undefined, now: Date): { endDate: string | null; ended: boolean; renewed: Renewal | null } {
  const clock = endDate ? clockTo(endDate, now) : null;
  if (!endDate || !clock || clock.days >= 0) return { endDate, ended: false, renewed: null };
  if (autoRenew !== true) return { endDate, ended: true, renewed: null };
  const months = termMonths(term);
  if (months === null) return { endDate: null, ended: false, renewed: { on: endDate, firstEnd: endDate, termMonths: null } };
  // Counted from the register's own date each time, so a day held to a short month never drifts.
  let k = 1;
  while (k < 1200 && (clockTo(addMonths(endDate, k * months), now)?.days ?? 0) < 0) k += 1;
  return { endDate: addMonths(endDate, k * months), ended: false, renewed: { on: addMonths(endDate, (k - 1) * months), firstEnd: endDate, termMonths: months } };
}

/**
 * What an agreement's date is called, from whether the register says it renews by itself (BUGS.md D160): 'Ends' where
 * it does not, 'Renews by itself' where it does, and 'Ends' with 'renewal not on record' where the register does not
 * say. `label` heads the opened card's term; `note` is said beside the date, or null.
 */
export function endWords(a: Pick<Agreement, 'autoRenew'>): { label: string; note: string | null } {
  if (a.autoRenew === true) return { label: 'Renews by itself', note: null };
  if (a.autoRenew === false) return { label: 'Ends', note: null };
  return { label: 'Ends', note: 'renewal not on record' };
}

/**
 * The agreements in force. Finance's model is the backbone (G6), so a line of its own is what
 * carries the entity and the term; the signed register's row is what carries the rest until the
 * paper has been read. Where the two name the same product, they are one agreement.
 */
export function agreementsInForce(c: CompanyRecordView, now: Date): Agreement[] {
  return agreementsOnFile(c, now).filter((a) => !a.ended);
}

/** The agreements that ran to their end date and did not renew by themselves, the latest to end first (U325). */
export function agreementsEnded(c: CompanyRecordView, now: Date): Agreement[] {
  return agreementsOnFile(c, now).filter((a) => a.ended).sort((a, b) => ((a.endDate ?? '') < (b.endDate ?? '') ? 1 : -1));
}

/** Every agreement on file, in force or ended, the soonest to end first. */
export function agreementsOnFile(c: CompanyRecordView, now: Date): Agreement[] {
  const terms = c.billing?.terms ?? [];
  const lines = c.billing?.lines ?? [];
  const titles = c.contracts.map((k) => k.product ?? k.dealTitle ?? 'Agreement');
  const claims = new Map<string, number>();
  for (const t of titles) { const l = lineFor(lines, t); if (l) claims.set(l.product, (claims.get(l.product) ?? 0) + 1); }
  const out: Agreement[] = [];
  for (const k of c.contracts) {
    // BUGS.md D47: an agreement filed from a signing notice, with no product, takes the name of the deal it closed.
    const title = k.product ?? k.dealTitle ?? 'Agreement';
    const term = terms.find((t) => t.product && sameProduct(t.product, title));
    const line = lineFor(lines, title);
    // A row that names BOTH a rights holder and the competition being licensed is an agency deal:
    // we resell somebody's content for a season and keep a share. Both columns, never one -- a
    // competition alone is a description, and a rights holder alone (Bet365's MODUS row) is a
    // running service billed by volume, whose fee the register writes as "Variable" anyway.
    const season = k.competition && k.rightsHolder ? { competition: k.competition, rightsHolder: k.rightsHolder } : null;
    const stands = standing(k.endDate ?? term?.contractEnd ?? null, k.autoRenew ?? null, k.initialTerm, now);
    const endDate = stands.endDate;
    out.push({
      id: k.id,
      title,
      endDate,
      clock: endDate ? clockTo(endDate, now) : null,
      ended: stands.ended,
      renewed: stands.renewed,
      autoRenew: k.autoRenew ?? null,
      entity: term?.company ?? null,
      start: k.startDate ?? term?.contractStart ?? null,
      billed: Boolean(term),
      feeText: k.monthlyFee,
      fee: parseFee(k.monthlyFee, season ? 'season' : 'month'),
      documentSignalId: k.documentSignalId,
      document: k.document ?? null,
      contractLink: k.contractLink,
      booked: line ? { product: line.product, amount: line.billed, shared: claims.get(line.product) ?? 1 } : null,
      season,
      noticeDays: k.noticeDays ?? null,
    });
  }
  return out.sort((a, b) => (a.clock?.days ?? 1e9) - (b.clock?.days ?? 1e9));
}

/**
 * The one product line of finance's that a register row names, or nothing. Two ways, in order:
 * the product itself ("InPlay" is finance's "In-Play"), then the parenthetical the register puts
 * after it, which is where Bet365's "Content Licencing (MODUS)" carries the word that finance
 * books £873,290 under. sameProduct folds brackets away, so the first test can never match on a
 * parenthetical by accident and the second is the only thing that reads one. Nothing here guesses:
 * where neither names a line the agreement carries no money, which is the true answer for the two
 * Bet365 content rows that finance has no line for at all.
 */
export function lineFor(lines: Array<{ product: string; billed: number }>, title: string): { product: string; billed: number } | null {
  const direct = lines.find((l) => sameProduct(l.product, title));
  if (direct) return direct;
  const inside = /\(([^)]+)\)/.exec(title)?.[1];
  return inside ? lines.find((l) => sameProduct(l.product, inside)) ?? null : null;
}

/** "In-Play" and "InPlay" and "In Play" are one product; so are "One OTT" and "OneOTT". */
export function sameProduct(a: string, b: string): boolean {
  const fold = (s: string) => s.toLowerCase().replace(/\([^)]*\)/g, '').replace(/[^a-z]/g, '');
  return fold(a) === fold(b);
}

/** Two rows a reader cannot tell apart are marked, so the page never looks like a rendering fault. */
export function markIndistinguishable(rows: Agreement[]): Array<Agreement & { twin: number | null; twins: number }> {
  // The fee is part of what tells two rows apart. Without it the record called Bet365's
  // £480,000-a-month content licence and its £12,000 one "identical in everything Cobrain holds
  // but their reference", which is the worst thing a page can do: state a falsehood confidently.
  const key = (a: Agreement) => `${a.title}|${a.endDate}|${a.start}|${a.entity}|${a.billed}|${a.feeText ?? ''}`;
  const counts = new Map<string, number>();
  for (const a of rows) counts.set(key(a), (counts.get(key(a)) ?? 0) + 1);
  const seen = new Map<string, number>();
  return rows.map((a) => {
    const k = key(a);
    const total = counts.get(k) ?? 1;
    if (total < 2) return { ...a, twin: null, twins: 1 };
    const n = (seen.get(k) ?? 0) + 1;
    seen.set(k, n);
    return { ...a, twin: n, twins: total };
  });
}

/* ---------------------------------------------------- usage, as two questions (module 15 move 5)
 * The panel used to draw eighteen months of bars for watched hours, active users, sessions and
 * event coverage, for both products, whether or not any of it had moved. Mike's word for it was
 * spurious. A company page has room for two questions about usage and no more: is this client
 * growing or dying, and is the licence they pay for being used. Everything below answers one of
 * those two and nothing else. */

export type Direction = 'up' | 'down' | 'flat';
export type YearOnYear = { period: string; then: string; value: number; was: number; pct: number; direction: Direction };

/**
 * The newest month that can be compared with the same month a year before it. Whole months only,
 * and never a month either side has marked not comparable: a break in the source is not a fall,
 * and drawing it as one is how a page invents a story. Returns null when the pair does not exist,
 * and the panel then says there is nothing to compare rather than reaching for a shorter window.
 */
export function yearOnYear<T extends { period: string; notComparable: string | null }>(months: T[], value: (m: T) => number | null): YearOnYear | null {
  const usable = months.filter((m) => !m.notComparable && value(m) !== null);
  for (let i = usable.length - 1; i >= 0; i--) {
    const now = usable[i]!;
    const [y, mo] = now.period.split('-');
    const before = usable.find((m) => m.period.slice(0, 7) === `${Number(y) - 1}-${mo}`);
    if (!before) continue;
    const v = value(now)!;
    const w = value(before)!;
    if (w === 0) continue;                                  // a rise from nothing has no percentage
    const pct = ((v - w) / w) * 100;
    // Inside two per cent either way is not a direction, it is noise wearing one.
    const direction: Direction = Math.abs(pct) < 2 ? 'flat' : pct > 0 ? 'up' : 'down';
    return { period: now.period, then: before.period, value: v, was: w, pct, direction };
  }
  return null;
}

/** "up 34%", "down 8%", "level" — the words, never a colour on its own. */
export function directionWords(y: YearOnYear): string {
  if (y.direction === 'flat') return 'level';
  return `${y.direction} ${Math.abs(Math.round(y.pct))}%`;
}

/** The licence position: what is paid for against what is used. Null where seats are not recorded. */
export function licencePosition(c: { licencesCount: number | null; activeUsers30d: number | null } | null):
  { seats: number; used: number; pct: number; spare: number } | null {
  if (!c || c.licencesCount === null || c.activeUsers30d === null || c.licencesCount <= 0) return null;
  return { seats: c.licencesCount, used: c.activeUsers30d, pct: Math.round((c.activeUsers30d / c.licencesCount) * 100), spare: c.licencesCount - c.activeUsers30d };
}

/* ------------------------------------------------ the register against what finance booked
 * Point 7 of Mike's brief: "The content licensing field is just spurious data and is not right."
 * He was right, and it is not one bad row. Measured on the live register on 2026-09-21: of the 92
 * companies carrying a live contract, only 22 have a fee that lands within a quarter of what
 * Anaplan booked for them; 13 imply MUCH more. Bet365 is the worst -- its register says £493,000 a
 * month against £11,500 booked in a year, and its £480,000 Content Licencing row is the single
 * largest fee in the whole register, the next being £100,000. Its InPlay row, £1,000 a month
 * against £11,500 booked, reconciles almost exactly: the register is not wrong everywhere, it is
 * wrong in places, and the page cannot tell which by looking at one side of it.
 *
 * So the page stops asserting the register's total as the company's worth. It states both and says
 * which way they disagree. Where they are within a quarter of each other it says so too, because
 * "these agree" is itself worth knowing. */

/**
 * Set where the comparison was made on some agreements' own finance lines and not on the company's whole booked
 * total (B1535): which agreements, which of finance's lines, and what finance booked from the company in all. The
 * head printed the compared lines' sum as the company's money -- 'The register and finance agree: £12,000 booked over
 * twelve months.' on Bet365, which finance billed £956,780 (BUGS.md D159) -- so the page must say what was compared
 * and give the company's total apart.
 */
export type Compared = { titles: string[]; lines: string[]; companyBooked: number };

export type Reconciliation =
  | { kind: 'agree'; contracted: number; booked: number; months: number; compared?: Compared }
  | { kind: 'register-higher'; contracted: number; booked: number; times: number; months: number; compared?: Compared }
  | { kind: 'register-lower'; contracted: number; booked: number; times: number; months: number; compared?: Compared }
  | { kind: 'nothing-booked'; contracted: number }
  | { kind: 'no-figure'; booked: number | null };

/**
 * Compare the register against the billing, A MONTH AGAINST A MONTH.
 *
 * This read a year against a year, and a year against a year is wrong whenever the client has not
 * been billed for a whole year. Measured on live: of the seventeen companies the page called
 * "register higher", Swish Analytics, Abraxas, Hard Rock and Shot Quality had each been billed for
 * FOUR months and were reported as three times over; Skill Corner and Gambit for six and reported
 * as two and four times over. Their contracts started in February and April. Nothing was wrong with
 * any of them -- the comparison was dividing a twelve-month rate by a four-month total and calling
 * the arithmetic a discrepancy.
 *
 * So the register's monthly total is compared with the average month finance actually booked, over
 * the months it actually booked. `months` is carried out so the page can say "billed for four of the
 * last twelve months" and the reader can see which window the figures came from.
 *
 * Sterling only: the register holds euro fees too and there is no rate in the store, so a mixed
 * company is reported as having no comparable figure rather than converted at a rate nobody chose.
 */
export function reconcile(rows: Agreement[], bookedLast12: number | null, monthsBilled = 12): Reconciliation {
  const { totals } = contractedAYear(rows);
  const gbp = totals.find((t) => t.currency === 'GBP');
  const onlyGbp = totals.length === 1 && gbp !== undefined;
  if (!onlyGbp || !gbp) return { kind: 'no-figure', booked: bookedLast12 };
  const contracted = gbp.amount;
  // BOTH SIDES ARE THE SAME AGREEMENTS. `rows` has already had the agency rows taken out, so taking
  // the company's whole `billedLast12` as the other side put finance's money for agreements the
  // register prices as "Variable" against a total that cannot include them: Bet365's £12,000 InPlay
  // row was weighed against £965,428, almost all of it the Modus row, and the page said the register
  // "accounts for only 1%". Where every row here carries its own line the money is added from those
  // lines instead, and Bet365 comes out at £12,000 against £11,500 -- which is agreement (B1535).
  const own = rows.filter((a) => a.fee !== null);
  const lines = own.map((a) => a.booked).filter((b): b is NonNullable<typeof b> => b !== null && b !== undefined);
  // Only when EVERY priced row has a line of its own: a partial sum would understate finance and
  // make the register look high again, which is the fault this whole pass exists to stop.
  const onOwnLines = own.length > 0 && lines.length === own.length;
  const bookedLast12Comparable = onOwnLines
    ? lines.reduce((n, l) => n + l.amount, 0)
    : bookedLast12;
  if (bookedLast12Comparable === null || bookedLast12Comparable <= 0) return { kind: 'nothing-booked', contracted };
  // A month against a month. Where the copy gives no month count, twelve is the old behaviour and
  // the comparison is unchanged.
  const months = monthsBilled > 0 ? Math.min(monthsBilled, 12) : 12;
  const times = (contracted / 12) / (bookedLast12Comparable / months);
  // Where the lines compared are not all the company's money, say which they were (D159). A cent's difference is
  // the same money, not a subset.
  const compared: Compared | undefined = onOwnLines && bookedLast12 !== null && bookedLast12 - bookedLast12Comparable >= 1
    ? {
        titles: own.map((a) => a.title),
        lines: [...new Set(own.map((a) => a.booked!.product))],
        companyBooked: bookedLast12,
      }
    : undefined;
  const scope = compared ? { compared } : {};
  if (times >= 0.75 && times <= 1.25) return { kind: 'agree', contracted, booked: bookedLast12Comparable, months, ...scope };
  if (times > 1.25) return { kind: 'register-higher', contracted, booked: bookedLast12Comparable, times, months, ...scope };
  return { kind: 'register-lower', contracted, booked: bookedLast12Comparable, times, months, ...scope };
}

/** "A", "A and B", "A, B and C". */
export function andList(xs: string[]): string {
  if (xs.length <= 1) return xs[0] ?? '';
  return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

/**
 * The head's sentence where only some agreements' own lines were compared (D159), as text, so the suite can hold it
 * to naming what was compared and to giving the company's booked total apart, never the compared sum as the
 * company's money. Bet365: "InPlay's £12,000 a year on the register matches finance's In-Play line, £12,000 over
 * twelve months. Finance booked £956,780 from them in all over twelve months; the other £944,780 is on lines the
 * register sets no fixed fee for."
 */
export function comparedWords(r: Extract<Reconciliation, { kind: 'agree' | 'register-higher' | 'register-lower' }>): string | null {
  const c = r.compared;
  if (!c) return null;
  const gbp = (n: number) => `£${Math.round(n).toLocaleString('en-GB')}`;
  const over = r.months < 12 ? `over ${r.months} ${r.months === 1 ? 'month' : 'months'}` : 'over twelve months';
  const whose = c.titles.length === 1 ? `${c.titles[0]}'s` : `${andList(c.titles)}'s`;
  const lineWord = c.lines.length === 1 ? 'line' : 'lines';
  const lines = `finance's ${andList(c.lines)} ${lineWord}`;
  const rest = c.companyBooked - r.booked;
  const all = `Finance booked ${gbp(c.companyBooked)} from them in all ${over}; the other ${gbp(rest)} is on lines the register sets no fixed fee for.`;
  switch (r.kind) {
    case 'agree':
      return `${whose} ${gbp(r.contracted)} a year on the register matches ${lines}, ${gbp(r.booked)} ${over}. ${all}`;
    case 'register-higher':
      return `Month for month the register's ${gbp(r.contracted)} a year for ${andList(c.titles)} claims ${r.times >= 2 ? `${timesWords(r.times)} as much as` : `${timesWords(r.times)} than`} ${lines}, ${gbp(r.booked)} ${over}, so its fee is not to be trusted. ${all}`;
    case 'register-lower':
      return `Month for month the register's ${gbp(r.contracted)} a year for ${andList(c.titles)} accounts for only ${Math.round(r.times * 100)}% of ${lines}, ${gbp(r.booked)} ${over}. ${all}`;
  }
}

/** How many times over, in words a person reads: "500 times", "3 times", "a third". */
export function timesWords(times: number): string {
  if (times >= 2) return `${Math.round(times).toLocaleString('en-GB')} times`;
  if (times > 1) return `${Math.round((times - 1) * 100)}% more`;
  if (times > 0) return `${Math.round((1 - times) * 100)}% less`;
  return 'nothing';
}

/* --------------------------------------------- what an agreement's money line may say (A37) */

/**
 * The money line on an agreement's card, and the fee in its opened detail.
 *
 * THE FIGURE IS ALWAYS SHOWN. A first attempt hedged it ("£480,000 a month on the register") and a
 * second removed it altogether, and the second was worse: Mike, immediately -- "You have removed all
 * revenues. This is not good enough." A revenue page that prints no revenue has not become honest,
 * it has become useless, and it was never what he asked for. What he asked for is that the money on
 * the page answers to the billing.
 *
 * So the contracted figure stands, and `corroborated` marks whether finance's own figures bear it
 * out. Doubt is carried BESIDE the number, never instead of it, and never by deleting it.
 *
 * This is a function and not a line of JSX so the suite can hold it to that: the tests assert the
 * figure is present in both states.
 */
/**
 * Whether finance's own figures bear the register out: true where they agree, false where they differ (the register
 * higher or lower), and 'nothing' where finance holds nothing to compare it with, which is no contradiction and is
 * never drawn as one (BUGS.md D167).
 */
export type Bearing = boolean | 'nothing';

export function cardMoney(a: Agreement, corroborated: Bearing): { text: string; untrusted: boolean } {
  // A season fee is said once, with the competition it buys. Saying "£480,000 a month · £5,760,000
  // a year" of a one-season licence was the largest false statement on the page.
  if (a.fee && a.season) return { text: `${feeLine(a.fee).month} for ${a.season.competition}`, untrusted: false };
  if (a.fee) { const f = feeLine(a.fee); return { text: `${f.month}, ${f.year}`, untrusted: corroborated === false }; }
  if (a.feeText) return { text: `Fee: ${a.feeText}`, untrusted: corroborated === false };
  return { text: 'No fee on the register row', untrusted: false };
}

/**
 * The BILLED row of one agreement. It says what finance booked AGAINST THIS AGREEMENT and nothing
 * else. It used to print `billedLast12`, the whole company's money, on any row that happened to
 * match an Anaplan term: on the live page Bet365's £1,000-a-month InPlay agreement read
 * "BILLED £965,428.16 over twelve months", which is almost entirely the Modus content deal three
 * rows above it -- and that row said "Nothing joined to it". A company total is never one
 * agreement's revenue, so it is not offered here as a fallback; where finance has no line for a
 * row the row says so, and says whether the company has money elsewhere.
 */
export function billedDetail(a: Agreement, companyBooked: number | null): { value: string; sub: string | undefined; words: boolean; none: boolean } {
  if (a.booked) {
    const value = `\u00a3${Math.round(a.booked.amount).toLocaleString('en-GB')}`;
    const sub = a.booked.shared > 1
      ? `over twelve months, on finance\u2019s \u201c${a.booked.product}\u201d line \u2014 which ${a.booked.shared} of these agreements name, so it is not this one\u2019s alone`
      : `over twelve months, on finance\u2019s \u201c${a.booked.product}\u201d line`;
    return { value, sub, words: false, none: false };
  }
  return {
    value: 'Nothing joined to it', words: false, none: true,
    sub: companyBooked !== null && companyBooked > 0
      ? 'finance books this company\u2019s money under other lines'
      : 'no billing name is joined to this company at all',
  };
}

/**
 * What the company is worth over these agreements. FINANCE LEADS, and the register follows it
 * (G6, "Anaplan is the only source of Truth"; A37, Mike on reading this very heading over Bet365:
 * "£5,916,000 a year is 100% not correct ... you need to map everything to the billing"). The
 * heading used to print the register's own total and nothing else, so the largest figure on the
 * page was the one finance contradicts by six times. The register's total is still SHOWN -- doubt
 * goes beside a number, never instead of it -- but second, and named as the register's.
 */
export function contractedRight(rows: Agreement[], ok: Bearing, booked: number | null): string | undefined {
  const { totals, agency, unpriced } = contractedAYear(rows);
  const money = booked !== null && booked > 0 ? `£${Math.round(booked).toLocaleString('en-GB')} booked in twelve months` : null;
  const reg = totals.length ? `the register says ${totalLine(totals)} a year${ok === false ? ', which finance does not bear out' : ''}` : null;
  // Named, and named as somebody else's. It is real money this company pays and it is not ours to
  // book, so it is neither added to the register total nor dropped from the page.
  const who = [...new Set(rows.filter((a) => a.season && a.fee).map((a) => a.season!.rightsHolder))];
  const sold = agency.length
    ? `${totalLine(agency)} more sold for ${who.length === 1 ? who[0]! : 'a rights holder'}, whose share finance books against them`
    : null;
  // BUGS.md D189: a fee the register writes in words ("Variable") is priced, in words, never "no fee on record".
  const worded = unpriced.filter((a) => a.feeText && a.feeText.trim());
  const kinds = [...new Set(worded.map((a) => a.feeText!.trim()))];
  const inWords = worded.length ? `${worded.length} priced as ${kinds.length === 1 ? kinds[0] : 'words, not a figure'}` : null;
  const blank = unpriced.length - worded.length;
  const none = blank ? `${blank} with no fee on record` : null;
  const parts = [money, reg, sold, inWords, none].filter(Boolean) as string[];
  return parts.length ? parts.join('; ') : undefined;
}

/** The Fee term in the opened card: the value, and the line under it. */
export function feeDetail(a: Agreement, corroborated: Bearing): { value: string; sub: string | undefined; words: boolean; none: boolean } {
  const doubt = corroborated === true ? '' : corroborated === false ? ' \u2014 finance\u2019s own figures do not bear it out' : '; finance holds nothing to compare it with';
  // Never doubted against the billing, because the billing is not of the same thing: we keep a share
  // and finance books that share against the rights holder. Bet365's four Uruguay buyers pay about
  // \u00a3670,000 between them and finance's Media Pro line for the same year is \u00a368,598, near enough
  // a tenth of it -- so the register's figure and the copy's can both be right and never agree.
  if (a.fee && a.season) return {
    value: feeLine(a.fee).month, words: false, none: false,
    sub: `for ${a.season.competition}, ${a.start && a.endDate ? 'over its term' : 'for the season'} \u2014 sold on behalf of ${a.season.rightsHolder}, who take the greater share, so finance books ours against them and not against this company`,
  };
  if (a.fee) return { value: feeLine(a.fee).month, sub: `${feeLine(a.fee).year} at this rate${doubt}`, words: false, none: false };
  if (a.feeText) return { value: a.feeText, sub: `the register states it in words, not as a figure${doubt}`, words: true, none: false };
  return { value: 'Not on the register row', sub: undefined, words: false, none: true };
}

/**
 * The card's Notice (BUGS.md D162): the register's notice days and the day notice must be given by, counted back from
 * the end date the card shows ("30 days, so notice by 1 Aug 2028"); "Not on the register" only where it holds none.
 */
/**
 * BUGS.md D51: the day notice is due on an agreement (its end date less the register's notice days), as the card's Notice
 * term counts it, while that day is still to come; null where the register holds no period or no end date, or the day has
 * passed. The agreement card's "Put a reminder before the notice date" writes its task due on this day.
 */
export function noticeBy(a: Pick<Agreement, 'noticeDays' | 'endDate'>, now: Date): string | null {
  if (a.noticeDays === null || !a.endDate) return null;
  const by = new Date(Date.parse(`${a.endDate.slice(0, 10)}T00:00:00Z`) - a.noticeDays * 86_400_000).toISOString().slice(0, 10);
  return by < now.toISOString().slice(0, 10) ? null : by;
}

export function noticeWords(a: Pick<Agreement, 'noticeDays' | 'endDate'>, now: Date): { value: string; sub?: string; none: boolean } {
  if (a.noticeDays === null) return { value: 'Not on the register', sub: 'The register holds no notice period for it', none: true };
  const days = `${a.noticeDays} ${a.noticeDays === 1 ? 'day' : 'days'}`;
  if (!a.endDate) return { value: days, sub: 'with no end date on record to count it from', none: false };
  const end = new Date(`${a.endDate.slice(0, 10)}T00:00:00Z`);
  const by = new Date(end.getTime() - a.noticeDays * 86_400_000).toISOString().slice(0, 10);
  const today = now.toISOString().slice(0, 10);
  return by < today
    ? { value: `${days}, so notice was due by ${plateDay(by)}`, sub: 'that day has passed', none: false }
    : { value: `${days}, so notice by ${plateDay(by)}`, none: false };
}

/**
 * A nightly copy that has not come through (drawing c5-states.html, "iGame's analytics could not be read" and "Finance's
 * figures could not be read"): where the last copy is more than 36 hours old, when it came and that nothing newer has, in
 * ink on a tint and never red. The store holds no failure flag, only when each copy last came, so the words say no more.
 */
export function staleWords(what: string, at: string | null, now: Date, when: (d: Date) => string): string | null {
  if (!at) return null;
  // Finance's copy time comes as Postgres prints it, "2026-09-18 12:08:07.503+00", which not every browser parses.
  const t = Date.parse(at.replace(' ', 'T').replace(/([+-]\d\d)$/, '$1:00'));
  if (Number.isNaN(t) || now.getTime() - t <= 36 * 3_600_000) return null;
  return `${what} last came through ${when(new Date(t))}, and nothing newer has since, so these may be out of date.`;
}
